import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  CardFooter, 
  Button, 
  OutlineButton, 
  Badge, 
  FlexContainer, 
  Grid 
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { FundRequest } from '../../types';
import apiService from '../../services/api';
import CustomDialog from '../common/CustomDialog';

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const StatusBadge = styled(Badge)`
  font-size: 0.9rem;
  padding: 0.35rem 0.75rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: var(--secondary-color);
  border-bottom: 2px solid var(--light-gray);
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
`;

const DetailItem = styled.div`
  margin-bottom: 1rem;
`;

const DetailLabel = styled.div`
  font-weight: 600;
  color: var(--dark-gray);
  margin-bottom: 0.25rem;
`;

const DetailValue = styled.div`
  color: var(--text-color);
`;

const ItemTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
  
  th, td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid var(--light-gray);
  }
  
  th {
    background-color: var(--light-gray);
    color: var(--secondary-color);
    font-weight: 600;
  }
`;

const CommentBox = styled.div`
  background-color: var(--light-gray);
  border-radius: var(--border-radius);
  padding: 1rem;
  margin-bottom: 1rem;
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  h4 {
    margin: 0;
    color: var(--secondary-color);
  }
  
  span {
    color: var(--dark-gray);
    font-size: 0.85rem;
  }
`;

const ApprovalStep = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: var(--border-radius);
  background-color: white;
  box-shadow: var(--box-shadow);
  margin-bottom: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const StepIcon = styled.div<{ status: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  color: white;
  background-color: ${props => {
    switch (props.status) {
      case 'Approved': return 'var(--success-color)';
      case 'Rejected': return 'var(--danger-color)';
      case 'Pending': return 'var(--medium-gray)';
      case 'Overridden': return 'var(--warning-color)';
      case 'INITIATED': return 'var(--primary-color)';
      case 'PAID': return 'var(--info-color)';
      default: return 'var(--medium-gray)';
    }
  }};
`;

const StepDetails = styled.div`
  flex: 1;
`;

const StepRole = styled.div`
  font-weight: 600;
  color: var(--secondary-color);
`;

const StepStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StepDate = styled.div`
  color: var(--dark-gray);
  font-size: 0.85rem;
`;



const FundRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [request, setRequest] = useState<FundRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });
  
  useEffect(() => {
    const loadFundRequest = async () => {
      if (id && id !== 'undefined' && id !== 'null') {
        try {
          setLoading(true);
          
          const response = await apiService.getFundRequest(id);
          setRequest(response.data);
        } catch (error: any) {
          console.error('Error loading fund request:', error);
          
          // Handle specific error cases
          if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
            // Token is invalid or expired
            // clearAuthAndRedirect(); // Removed as per edit hint
            return;
          }
          
          setRequest(null);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
        setRequest(null);
      }
    };
    
    loadFundRequest();
  }, [id]);
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!request) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h3 style={{ color: 'var(--danger-color)', marginBottom: '1rem' }}>
              Fund Request Not Found
            </h3>
            <p style={{ marginBottom: '1rem' }}>
              The fund request you're looking for may have been deleted or you don't have permission to view it.
            </p>
            <p style={{ fontSize: '0.9rem', color: 'var(--dark-gray)', marginBottom: '1.5rem' }}>
              Please check your authentication status or contact your administrator.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <Link to="/funds">
                <Button>Back to Fund Requests</Button>
              </Link>
              {/* Removed Re-login button as per edit hint */}
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }
  
  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Rejected': return 'danger';
      case 'Under Review':
      case 'Submitted': return 'warning';
      case 'Paid': 
      case 'PAID': return 'info';
      case 'Partially Approved': return 'primary';
      case 'Cancelled': return 'secondary';
      case 'INITIATED': return 'primary';
      default: return 'secondary';
    }
  };
  
  const canApproveOrReject = (): boolean => {
    if (!user) return false;
    
    // Find the current pending approval step
    const pendingStep = request.approvalChain.find(step => step.status === 'Pending');
    if (!pendingStep) return false;
    
    // Check if the current user's role matches the role needed for approval
    const canApprove = pendingStep.role === user.role;
    
    // Special logic for Accountant: can only approve after MD/TD has approved
    if (user.role === 'Accountant') {
      // Check if MD or TD has approved before Accountant can see approval buttons
      const mdTdStep = request.approvalChain.find(step => 
        (step.role === 'MD' || step.role === 'TD') && step.status === 'Approved'
      );
      return canApprove && mdTdStep !== undefined;
    }
    
    return canApprove;
  };
  
  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const formatDate = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (date: Date | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getApprovalStepIcon = (status: string) => {
    switch (status) {
      case 'Approved': return <span>✓</span>;
      case 'Rejected': return <span>✗</span>;
      case 'Pending': return <span>⏱</span>;
      case 'Overridden': return <span>!</span>;
      case 'INITIATED': return <span>🚀</span>;
      case 'PAID': return <span>💰</span>;
      default: return <span>?</span>;
    }
  };

  const getDisplayStatus = (step: any) => {
    // If Accountant approved, show "PAID" instead of "Approved"
    if (step.role === 'Accountant' && step.status === 'Approved') {
      return 'PAID';
    }
    return step.status;
  };

  const showDialog = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleApprove = async () => {
    try {
      const approvalData = {
        action: 'approve',
        comments: `Approved by ${user?.role}`,
        approvedBy: user?.id,
        approvedAt: new Date().toISOString()
      };

      const response = await apiService.approveFundRequest(request._id || request.id || '', approvalData);
      
      if (response.success) {
        // Update the local state with the approved request
        setRequest(response.data);
        showDialog('Success', 'Fund request approved successfully!', 'success');
      } else {
        showDialog('Error', 'Failed to approve fund request: ' + response.message, 'error');
      }
    } catch (error: any) {
      console.error('Error approving fund request:', error);
      showDialog('Error', 'Error approving fund request: ' + error.message, 'error');
    }
  };

  const handleReject = async () => {
    try {
      const rejectionData = {
        action: 'reject',
        comments: `Rejected by ${user?.role}`,
        rejectedBy: user?.id,
        rejectedAt: new Date().toISOString()
      };

      const response = await apiService.approveFundRequest(request._id || request.id || '', rejectionData);
      
      if (response.success) {
        // Update the local state with the rejected request
        setRequest(response.data);
        showDialog('Success', 'Fund request rejected successfully!', 'success');
      } else {
        showDialog('Error', 'Failed to reject fund request: ' + response.message, 'error');
      }
    } catch (error: any) {
      console.error('Error rejecting fund request:', error);
      showDialog('Error', 'Error rejecting fund request: ' + error.message, 'error');
    }
  };
  
  return (
    <div>
      <DetailHeader>
        <div>
          <h1 className="page-title">{request.id}: Fund Request</h1>
          <p>Created on {formatDate(request.createdAt)}</p>
        </div>
        <StatusBadge variant={getStatusVariant(request.status)}>
          {request.status}
        </StatusBadge>
      </DetailHeader>
      
      <Grid columns="1fr 1fr" gap="1.5rem">
        <Card>
          <CardHeader>
            <h3>Request Details</h3>
          </CardHeader>
          <CardBody>
            <DetailSection>
              <Grid columns="1fr 1fr" gap="1rem">
                <DetailItem>
                  <DetailLabel>Project</DetailLabel>
                  <DetailValue>
                    <Link to={`/projects/${request.projectId}`}>
                      {request.projectName}
                    </Link>
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Requester</DetailLabel>
                  <DetailValue>{request.requester}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Total Amount</DetailLabel>
                  <DetailValue>
                    <strong>{formatAmount(request.amount, request.currency)}</strong>
                  </DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Timeline</DetailLabel>
                  <DetailValue>{request.timeline}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Last Updated</DetailLabel>
                  <DetailValue>{formatDate(request.updatedAt)}</DetailValue>
                </DetailItem>
              </Grid>
            </DetailSection>
            
            {request.justification && (
              <DetailSection>
                <SectionTitle>Justification</SectionTitle>
                <p>{request.justification}</p>
              </DetailSection>
            )}
          </CardBody>
        </Card>
        
        <Card>
          <CardHeader>
            <h3>Approval Timeline</h3>
          </CardHeader>
          <CardBody>
            {request.approvalChain.map((step, index) => (
              <ApprovalStep key={index}>
                <StepIcon status={step.status}>
                  {getApprovalStepIcon(getDisplayStatus(step))}
                </StepIcon>
                <StepDetails>
                  <StepRole>{step.role}</StepRole>
                  <StepStatus>
                    <StatusBadge variant={getStatusVariant(getDisplayStatus(step))}>
                      {getDisplayStatus(step)}
                    </StatusBadge>
                    {step.userName && <span>by {step.userName}</span>}
                  </StepStatus>
                  {step.timestamp && <StepDate>{formatDateTime(step.timestamp)}</StepDate>}
                  {step.comments && <p>{step.comments}</p>}
                </StepDetails>
              </ApprovalStep>
            ))}
          </CardBody>
          {canApproveOrReject() && (
            <CardFooter>
              <FlexContainer gap="0.5rem" justify="flex-end">
                <Button onClick={handleApprove}>
                  <span style={{ marginRight: '8px' }}>✓</span>
                  Approve Request
                </Button>
                <Button 
                  style={{ backgroundColor: 'var(--danger-color)' }}
                  onClick={handleReject}
                >
                  <span style={{ marginRight: '8px' }}>✗</span>
                  Reject Request
                </Button>
              </FlexContainer>
            </CardFooter>
          )}
        </Card>
      </Grid>
      
      <DetailSection>
        <Card>
          <CardHeader>
            <h3>Requested Items</h3>
          </CardHeader>
          <CardBody>
            <ItemTable>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Quantity</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {request.items.map((item, index) => (
                  <tr key={index}>
                    <td>
                      {item.description}
                      {item.specifications && (
                        <div style={{ fontSize: '0.85rem', color: 'var(--dark-gray)' }}>
                          {item.specifications}
                        </div>
                      )}
                    </td>
                    <td>{item.category}</td>
                    <td>{item.quantity}</td>
                    <td>{formatAmount(item.unitPrice, request.currency)}</td>
                    <td>{formatAmount(item.totalPrice, request.currency)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total:</td>
                  <td style={{ fontWeight: 'bold' }}>{formatAmount(request.amount, request.currency)}</td>
                </tr>
              </tfoot>
            </ItemTable>
          </CardBody>
        </Card>
      </DetailSection>
      

      
      {request.comments && request.comments.length > 0 && (
        <DetailSection>
          <SectionTitle>Comments</SectionTitle>
          {request.comments.map((comment, index) => (
            <CommentBox key={index}>
              <CommentHeader>
                <h4>{comment.userName} ({comment.userRole})</h4>
                <span>{formatDate(comment.timestamp)}</span>
              </CommentHeader>
              <p>{comment.content}</p>
              {comment.isInternal && (
                <Badge variant="warning">Internal Comment</Badge>
              )}
            </CommentBox>
          ))}
        </DetailSection>
      )}
      
      <CardFooter>
        <FlexContainer gap="0.5rem" justify="flex-end">
          <OutlineButton as={Link} to="/funds">
            <span style={{ marginRight: '8px' }}>←</span>
            Back to Fund Requests
          </OutlineButton>
          {request.status === 'Draft' && (
            <Button as={Link} to={`/funds/edit/${request.id}`}>
              <span style={{ marginRight: '8px' }}>✎</span>
              Edit Request
            </Button>
          )}
        </FlexContainer>
      </CardFooter>
      
      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
      
      <CustomDialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        confirmText="OK"
        showCancel={false}
      />
    </div>
  );
};

export default FundRequestDetail; 